import Box from "@/common/components/Box";
import Button from "@/common/components/Button";
import Checkbox from "@/common/components/Checkbox";
import { ErrorMessage } from '@/common/components/ErrorMessage';
import { ImageUploader } from "@/common/components/ImageUploader";
import MaskedTextField from "@/common/components/MaskedTextField";
import { ModalMessageSimple } from "@/common/components/ModalMessageSimple";
import SelectController from "@/common/components/SelectController";
import { ShowHeader } from "@/common/components/ShowHeader";
import TextField from "@/common/components/TextField";
import { nameMask, phoneMask } from "@/common/constants/masks.constant";
import useBlobs from "@/common/hooks/queries/useBlobs";
import useCompany from "@/common/hooks/queries/useCompany";
import { useDriverLicenseCategories } from "@/common/hooks/queries/useDriverLicenseCategories";
import useDebounce from "@/common/hooks/useDebounce";
import { useGoBack } from "@/common/hooks/useGoBack";
import { Mapper } from "@/common/services/mapper";
import { CompanyBranchData } from "@/common/types/companyBranches.type";
import { DriverLicenseCategoryData } from "@/common/types/driverLicenseCategory";
import { RoleData } from "@/common/types/roles";
import { checkIsImage } from "@/common/utils/fileValidator";
import { FormTransformers } from "@/common/utils/formTransformers";
import { removeSpecialCharacters } from '@/common/utils/unFormatters';
import { useAvailableCompanyBranches } from "@/features/privateCompanies/hooks/companyBranches/useAvailableCompanyBranches";
import { useCompanyUserRoles } from "@/features/privateCompanies/hooks/useCompanyUserRoles";
import useCreateCompanyUser from "@/features/privateCompanies/hooks/useCreateCompanyUser";
import { companyUserSchema, CompanyUserValidationSchema } from "@/features/privateCompanies/schemas/create.schema";
import { statusOptions } from "@/features/users/constants/statusOptions.constant";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { useParams } from "react-router-dom";

const Create = () => {
  const { id } = useParams();
  const goBack = useGoBack();
  const queryClient = useQueryClient();

  const [file, setFile] = useState<Blob>();
  const [profilePictureBlobId, setProfilePictureBlobId] = useState<string>();

  const { mutateAsync: uploadBlob, isPending: uploadBlobIsPending } = useBlobs();

  const [messages, setMessages] = useState<string[] | boolean>(false);
  const [isError, setIsError] = useState<boolean>(false);

  const [searchBranch, setSearchBranch] = useDebounce('');
  const [searchRoles, setSearchRoles] = useDebounce('');
  const [searchDriverLicenseCategories, setSearchDriverLicenseCategories] = useDebounce('');

  const {
    data: company,
    isPending: isCompanyPending,
  } = useCompany({ id: id || "" });

  const {
    data: companyBranchOptions,
  } = useAvailableCompanyBranches({
    companyId: id,
    filters: {
      search: searchBranch,
    },
    mapper: Mapper.mapToOptions<CompanyBranchData>({
      labelFieldName: 'display_name',
      valueFieldName: 'id',
    }),
  });

  const {
    data: rolesOptions,
  } = useCompanyUserRoles({
    filters: {
      search: searchRoles,
    },
    mapper: Mapper.mapToOptions<RoleData>({
      labelFieldName: 'display_name',
      valueFieldName: 'id',
    })
  });

  const {
    data: driverLicenseCategoriesOptions
  } = useDriverLicenseCategories({
    filters: {
      search: searchDriverLicenseCategories,
    },
    mapper: Mapper.mapToOptions<DriverLicenseCategoryData>({
      labelFieldName: 'display_name',
      valueFieldName: 'id',
    })
  })

  const {
    register,
    handleSubmit,
    control,
    setError,
    watch,
    setValue,
    formState: { errors }
  } = useForm<CompanyUserValidationSchema>({
    resolver: zodResolver(companyUserSchema),
    defaultValues: {
      send_passwords_on_creation: true
    }
  });

  const sendPassword = watch('send_passwords_on_creation');

  const {
    mutate: createCompanyUser,
    isPending: createCompanyUserIsPending,
  } = useCreateCompanyUser({
    companyId: id,
    profilePictureBlobId,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['companyUsers'] });

      setIsError(false);

      if (sendPassword) {
        setMessages(['Senha enviada ao usuário por e-mail e/ou SMS.']);
      } else {
        goBack({
          fallback: `/companies/${id}/users`,
        });
      }
    },
    onError: (error) => {
      FormTransformers.errorsTransformer<CompanyUserValidationSchema>({
        setError,
        setMessages,
      })(error);

      setIsError(true);
    },
  });

  const onSubmit: SubmitHandler<CompanyUserValidationSchema> = (data) => createCompanyUser(data);

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        <ShowHeader
          goBack
          goBackProps={{ fallback: `/companies/${id}/users` }}
          isLoading={isCompanyPending}
          titleClassName="font-normal text-md text-white-400"
          title={company?.general_information?.fantasy_name}
          subtitle="Novo usuário"
          buttons={(
            <Button
              name="submit"
              type="submit"
              className="shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)]"
              isLoading={createCompanyUserIsPending}
              disabled={createCompanyUserIsPending || uploadBlobIsPending}
            >
              Confirmar
            </Button>
          )}
        />

        <Box className="flex flex-col gap-4 shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)] overflow-y-auto h-[calc(100vh-128px)]">
          <p className="text-lg font-bold">Dados pessoais</p>
          <div className='flex flex-col gap-1'>
            <ImageUploader
              isLoading={false}
              label="Foto"
              name="profile-picture"
              accept="image/*"
              onChange={async (e) => {
                const file = (e.target as HTMLInputElement & { files: FileList }).files?.[0]
                if (!file || !checkIsImage(file)) {
                  return
                }
                setFile(file)
                const response = await uploadBlob({
                  file: file as File,
                  filename: (file as File).name,
                });
                setProfilePictureBlobId(response?.data?.data?.id)
              }}
              currentImageUrl={file
                ? URL.createObjectURL(file)
                : undefined
              }
              disabled={createCompanyUserIsPending || uploadBlobIsPending}
            />

            {errors.profile_picture?.message && <ErrorMessage name='profile_picture' message={errors.profile_picture?.message as string} />}
          </div>
          <div className="flex gap-2 w-full justify-between">
            <div className="w-3/4">
              <MaskedTextField
                label="CPF"
                markAsRequired
                mask='000.000.000-00'
                errorMessage={errors.cpf?.message || undefined}
                {...register('cpf')}
                disabled={createCompanyUserIsPending}
              />
              <Button variant='link'>Importar dados</Button>
            </div>
            <MaskedTextField
              label="Data de nascimento"
              markAsRequired
              mask='00/00/0000'
              className="w-1/4"
              errorMessage={errors.date_of_birth?.message || undefined}
              {...register('date_of_birth')}
              disabled={createCompanyUserIsPending}
            />
          </div>
          <div className="flex gap-2 w-full justify-between">
            <MaskedTextField
              className="w-3/4"
              label="Nome"
              markAsRequired
              mask={nameMask}
              maxLength={64}
              errorMessage={errors.full_name?.message || undefined}
              {...register('full_name')}
              disabled={createCompanyUserIsPending}
            />
            <SelectController
              control={control}
              name="roles"
              label="Perfil"
              markAsRequired
              className="w-1/4"
              options={rolesOptions || []}
              errorMessage={errors.roles?.message || undefined}
              isMulti
              onInputChange={setSearchRoles}
            />
          </div>
          <SelectController
            control={control}
            name="status"
            label="Status"
            markAsRequired
            options={statusOptions}
            errorMessage={errors.status?.message || undefined}
            isDisabled={createCompanyUserIsPending}
          />
          <TextField
            label="Matrícula"
            maxLength={64}
            {...register('enrollment_number')}
            errorMessage={errors.enrollment_number?.message || undefined}
            disabled={createCompanyUserIsPending}
          />
          <SelectController
            control={control}
            name="branch_id"
            label="Filial"
            markAsRequired
            options={companyBranchOptions || []}
            errorMessage={errors.branch_id?.message || undefined}
            isDisabled={createCompanyUserIsPending}
            onInputChange={setSearchBranch}
          />
          <MaskedTextField
            label="Telefone"
            mask={phoneMask}
            errorMessage={errors.phone?.number?.message || undefined}
            {...register('phone.number')}
            disabled={createCompanyUserIsPending}
          />
          <TextField
            label="Email"
            errorMessage={errors.email?.message || undefined}
            {...register('email')}
            disabled={createCompanyUserIsPending}
          />
          <Checkbox
            label="Enviar credenciais via E-mail/SMS"
            {...register("send_passwords_on_creation")}
            disabled={createCompanyUserIsPending}
          />

          <div className="flex gap-2 w-full justify-between">
            <MaskedTextField
              label="Número da CNH"
              mask="00000000000"
              className="grow"
              errorMessage={errors.current_driver_license_registration_number?.message || undefined}
              {...register('current_driver_license_registration_number')}
              disabled={createCompanyUserIsPending}
            />

            <SelectController
              control={control}
              name="user_driver_license_categories"
              label="Categorias da CNH"
              onInputChange={setSearchDriverLicenseCategories}
              options={driverLicenseCategoriesOptions || []}
              isMulti
              className="grow"
              errorMessage={errors.user_driver_license_categories?.message || undefined}
              isDisabled={createCompanyUserIsPending}
            />

            <MaskedTextField
              label="Data de vencimento"
              mask="00/00/0000"
              className="grow"
              errorMessage={errors.current_driver_license_expiration_date?.message || undefined}
              {...register('current_driver_license_expiration_date')}
              disabled={createCompanyUserIsPending}
            />
          </div>

          <div className='flex flex-col gap-1'>
            <TextField
              label="Nome que será impresso no cartão"
              maxLength={60}
              {...register('name_on_card')}
              onChange={(event) => {
                setValue('name_on_card', removeSpecialCharacters(
                  event.currentTarget.value,
                  { includeSpaces: true }
                ));
              }}
              valueClassName="uppercase"
              errorMessage={errors.name_on_card?.message || undefined}
              disabled={createCompanyUserIsPending}
            />

            <p className='text-xs'>
              Limite máximo de 60 caracteres
            </p>
          </div>
        </Box>
      </form>

      {messages && (
        <ModalMessageSimple
          messages={messages}
          setMessages={setMessages}
          isError={isError}
          onClose={isError ? undefined : () => goBack({
            fallback: `/companies/${id}/users`,
          })}
        />
      )}
    </>
  );
};

export default Create;
